<template lang="pug">
.patient-registry
  .pr__header
    div
      h1.pr__title Padrón de pacientes
      p.pr__hint Vecinos registrados en Salud Campana. Filtrá y descargá la nómina en Excel.
    button.pr__export(@click="exportCsv" :disabled="exporting || !enabled")
      micon(name="attach_file")
      span {{ exporting ? "Generando…" : "Descargar CSV" }}

  .pr__notice(v-if="!enabled")
    p El padrón de pacientes no está disponible en este ambiente.

  template(v-else)
    .pr__filters
      input.pr__search(
        type="text"
        v-model="filters.search"
        placeholder="Buscar por nombre, DNI o CUIL"
        @keyup.enter="applyFilters"
      )
      select.pr__filter(v-model="filters.condition" @change="applyFilters")
        option(value="") Patología: todas
        option(v-for="c in catalog" :key="c._id" :value="c._id") {{ c.name }}
      select.pr__filter(v-model="filters.verified" @change="applyFilters")
        option(v-for="o in verifiedOptions" :key="o.value" :value="o.value") {{ o.label }}
      label.pr__date-label Registro
      input.pr__date(type="date" v-model="filters.registeredFrom" @change="applyFilters")
      span.pr__date-sep →
      input.pr__date(type="date" v-model="filters.registeredTo" @change="applyFilters")
      button.pr__apply(@click="applyFilters") Buscar
      button.pr__clear(@click="clearFilters") Limpiar

    .pr__summary
      strong {{ total }}
      span {{ total === 1 ? 'paciente' : 'pacientes' }}

    .pr__loading(v-if="loading") Cargando…

    .pr__empty(v-else-if="!rows.length") No hay pacientes para estos filtros.

    .pr__table-wrap(v-else)
      table.pr__table
        thead
          tr
            th Paciente
            th DNI
            th CUIL
            th Sexo
            th Nacimiento
            th Edad
            th Contacto
            th Domicilio
            th Cobertura
            th Patologías
            th Grupo familiar
            th Padrón
            th Registro
        tbody
          tr.pr__row(v-for="row in rows" :key="row._id")
            td
              router-link.pr__name(:to="{ name: 'patient-dashboard', params: { id: row._id } }") {{ fullName(row) }}
            td {{ row.dni }}
            td {{ row.cuil }}
            td {{ genderLabel(row.gender) }}
            td {{ formatDay(row.bornAt, true) }}
            td {{ row.age === null ? '' : row.age }}
            td
              div(v-if="row.phone") {{ row.phone }}
              div.pr__muted(v-if="row.email") {{ row.email }}
            td {{ formatAddress(row.address) }}
            td {{ row.coverage }}
            td
              span.pr__chip(v-for="name in row.chronicConditions" :key="name") {{ name }}
            td {{ row.familyGroupHolderDni ? 'Titular ' + row.familyGroupHolderDni : '' }}
            td
              span.pr__badge(:class="row.padronVerifiedAt ? 'is-ok' : 'is-no'") {{ row.padronVerifiedAt ? 'Sí' : 'No' }}
            td {{ formatDay(row.createdAt) }}

    .pr__pager(v-if="total > pageSize")
      button.pr__page-btn(:disabled="page === 1 || loading" @click="goTo(page - 1)") Anterior
      span.pr__page-info Página {{ page }} de {{ pages }}
      button.pr__page-btn(:disabled="page >= pages || loading" @click="goTo(page + 1)") Siguiente
</template>

<script>
import {
  GENDER_LABELS,
  VERIFIED_OPTIONS,
  emptyFilters,
  hasPatientRegistryCapability,
  buildRegistryParams,
  fullName,
  formatDay,
  formatAddress,
  csvFilename,
  list,
  exportCsv,
} from "@/services/patient-registry";
import { loadConditionsCatalog } from "@/services/patient-conditions";

/** @type {number} Filas por página. */
const PAGE_SIZE = 50;

/**
 * @component PatientRegistry
 * @description UC-REG-315 — Módulo "Padrón de pacientes" (Campana): el
 *   usuario interno consulta la nómina de vecinos registrados (búsqueda,
 *   patología, verificación por padrón, rango de registro, paginación) y la
 *   descarga como CSV para Excel. Visible solo con la capacidad
 *   `patientRegistry` que expone la API.
 * @example <PatientRegistry />
 */
export default {
  name: "PatientRegistry",
  data() {
    return {
      loading: false,
      exporting: false,
      filters: emptyFilters(),
      /** @type {Array<{_id:string,name:string}>} catálogo de patologías */
      catalog: [],
      rows: [],
      total: 0,
      page: 1,
      pageSize: PAGE_SIZE,
      verifiedOptions: VERIFIED_OPTIONS,
    };
  },
  computed: {
    /** @returns {boolean} capacidad del tenant (API decide) */
    enabled() {
      return hasPatientRegistryCapability(
        this.$store.state.organizationContext
      );
    },
    /** @returns {number} cantidad de páginas */
    pages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
  },
  async created() {
    if (!this.enabled) return;
    loadConditionsCatalog()
      .then((catalog) => {
        this.catalog = catalog;
      })
      .catch(() => {
        this.catalog = [];
      });
    await this.load();
  },
  methods: {
    fullName,
    formatDay,
    formatAddress,
    /**
     * @description Etiqueta del sexo.
     * @param {string} g
     * @returns {string}
     */
    genderLabel(g) {
      return GENDER_LABELS[g] || "";
    },
    /**
     * @description Params de la página actual.
     * @returns {Object}
     */
    params() {
      return buildRegistryParams(this.filters, {
        limit: this.pageSize,
        offset: (this.page - 1) * this.pageSize,
      });
    },
    /** @description Carga la página actual. */
    async load() {
      this.loading = true;
      try {
        const res = await list(this.params());
        this.rows = res.docs || [];
        this.total = res.total || 0;
      } catch (e) {
        this.rows = [];
        this.total = 0;
        this.$message.error("No pudimos cargar el padrón.");
      } finally {
        this.loading = false;
      }
    },
    /** @description Aplica los filtros desde la primera página. */
    applyFilters() {
      this.page = 1;
      return this.load();
    },
    /** @description Limpia los filtros. */
    clearFilters() {
      this.filters = emptyFilters();
      return this.applyFilters();
    },
    /**
     * @description Va a una página.
     * @param {number} page
     */
    goTo(page) {
      this.page = Math.min(Math.max(1, page), this.pages);
      return this.load();
    },
    /** @description Descarga el CSV con los filtros vigentes. */
    async exportCsv() {
      this.exporting = true;
      try {
        const blob = await exportCsv(buildRegistryParams(this.filters));
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = csvFilename();
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.$message.error("No se pudo generar el CSV.");
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.patient-registry {
  padding: 24px;
}
.pr__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}
.pr__title {
  margin: 0 0 4px;
  font-size: 22px;
  font-weight: 700;
}
.pr__hint {
  margin: 0;
  color: #6b7280;
}
.pr__export,
.pr__apply,
.pr__clear,
.pr__page-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  font-size: 14px;
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}
.pr__export,
.pr__apply {
  background: #4f46e5;
  border-color: #4f46e5;
  color: #fff;
  font-weight: 600;
}
.pr__notice,
.pr__empty,
.pr__loading {
  padding: 16px;
  border-radius: 10px;
  background: #f3f4f6;
  color: #4b5563;
}
.pr__filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.pr__search,
.pr__filter,
.pr__date {
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background: #fff;
}
.pr__search {
  min-width: 260px;
}
.pr__date-label,
.pr__date-sep {
  color: #6b7280;
  font-size: 13px;
}
.pr__summary {
  margin: 4px 0 12px;
  color: #374151;
  strong {
    margin-right: 4px;
  }
}
.pr__table-wrap {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
}
.pr__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th,
  td {
    padding: 10px 12px;
    text-align: left;
    border-bottom: 1px solid #f3f4f6;
    vertical-align: top;
    white-space: nowrap;
  }
  th {
    background: #f9fafb;
    color: #6b7280;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
  }
}
.pr__name {
  font-weight: 600;
  color: #4f46e5;
}
.pr__muted {
  color: #6b7280;
  font-size: 12px;
}
.pr__chip {
  display: inline-block;
  margin: 0 4px 4px 0;
  padding: 2px 8px;
  border-radius: 999px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 12px;
}
.pr__badge {
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  &.is-ok {
    background: #dcfce7;
    color: #166534;
  }
  &.is-no {
    background: #f3f4f6;
    color: #6b7280;
  }
}
.pr__pager {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
}
.pr__page-info {
  color: #6b7280;
  font-size: 13px;
}
</style>
